import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { createTimeEntry } from '../../../services/contractService';
import toast from 'react-hot-toast';

const TimeEntryForm = ({ contracts, expertId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const selectedContract = contracts?.find((c) => c?.id === data?.contractId);

      if (!selectedContract) {
        throw new Error('Contract not found');
      }

      const result = await createTimeEntry({
        contractId: data?.contractId,
        expertId,
        workDate: data?.workDate,
        minutes: parseInt(data?.hours) * 60 + parseInt(data?.minutes),
        description: data?.description,
        hourlyRate: selectedContract?.hourly_rate
      });

      if (!result?.success) {
        throw new Error(result?.error);
      }

      toast?.success('Time entry logged successfully');
      reset();
      onSuccess?.();
    } catch (error) {
      console.error('Create time entry error:', error);
      toast?.error(error?.message || 'Failed to log time entry');
    } finally {
      setLoading(false);
    }
  };

  if (contracts?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Clock" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Active Hourly Contracts</h3>
        <p className="text-muted-foreground">
          You need an active hourly contract to log time entries.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <h3 className="text-lg font-semibold text-foreground mb-4">Log Time Entry</h3>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Contract <span className="text-error">*</span>
          </label>
          <Select
            {...register('contractId', { required: 'Contract is required' })}
            error={errors?.contractId?.message}
          >
            <option value="">Select contract</option>
            {contracts?.map((contract) => (
              <option key={contract?.id} value={contract?.id}>
                {contract?.title} - ${contract?.hourly_rate?.toFixed(2)}/hr
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Work Date <span className="text-error">*</span>
          </label>
          <Input
            type="date"
            {...register('workDate', { required: 'Work date is required' })}
            error={errors?.workDate?.message}
            max={new Date()?.toISOString()?.split('T')?.[0]}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Hours <span className="text-error">*</span>
            </label>
            <Input
              type="number"
              min="0"
              max="24"
              {...register('hours', {
                required: 'Hours is required',
                min: { value: 0, message: 'Hours must be at least 0' },
                max: { value: 24, message: 'Hours cannot exceed 24' }
              })}
              error={errors?.hours?.message}
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Minutes <span className="text-error">*</span>
            </label>
            <Input
              type="number"
              min="0"
              max="59"
              step="15"
              {...register('minutes', {
                required: 'Minutes is required',
                min: { value: 0, message: 'Minutes must be at least 0' },
                max: { value: 59, message: 'Minutes cannot exceed 59' }
              })}
              error={errors?.minutes?.message}
              placeholder="0"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Description <span className="text-error">*</span>
          </label>
          <textarea
            {...register('description', {
              required: 'Description is required',
              minLength: { value: 10, message: 'Description must be at least 10 characters' }
            })}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
            rows="4"
            placeholder="Describe the work you performed..."
          />
          {errors?.description && (
            <p className="text-sm text-error mt-1">{errors?.description?.message}</p>
          )}
        </div>

        <Button type="submit" disabled={loading} iconName="Clock">
          {loading ? 'Logging...' : 'Log Time Entry'}
        </Button>
      </form>
    </div>
  );
};

export default TimeEntryForm;